import { action, computed, observable } from 'mobx'
import { filter, switchMap, type Observable } from 'rxjs'
import type { Id } from '@/infra/id'
import { type Message } from '@/features/messaging/Message'
import { type JobProgressionStore } from '@/features/jobs/taxes/progression/JobProgressionStore'
import type { MessageBankDialogViewModelFactory } from '@/features/messaging'
import type { ConversationViewModel } from '@/features/messaging/ConversationViewModel'
import type {
  DesktopNotificationState,
  SetDesktopNotificationsEnabledFn,
} from '@/features/notifications'
import type {
  AlertErrorHandlerFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { tapAction } from '@/utils/mobx-util'
import { withAbortSignal } from '@/utils/rx-util'

/**
 * The job's conversation, with the composer's message bank and the desktop
 * notifications for new messages.
 */
export class JobConversationViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  @observable.ref
  private accessor _desktopNotificationState: DesktopNotificationState = {
    enabled: false,
    denied: false,
  }

  constructor(
    jobId: Id,
    public readonly conversationVm: ConversationViewModel,
    public readonly messageBankDialogVm: ReturnType<MessageBankDialogViewModelFactory>,
    desktopNotificationState$: Observable<DesktopNotificationState>,
    private readonly _setDesktopNotificationsEnabled: SetDesktopNotificationsEnabledFn,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    createAlertRetryWrapper: AlertRetryWrapperFactory,
    progressionStore: JobProgressionStore,
    newMessages$: Observable<Message>,
  ) {
    this._lifecycle.manage(conversationVm)
    this._lifecycle.manage(
      // Progression messages show the job's steps, which have no realtime
      // updates of their own.
      newMessages$.pipe(
        filter(
          (m) =>
            m.content.type === 'Progression' &&
            m.conversation.meta.type === 'Job' &&
            m.conversation.meta.id === jobId,
        ),
        switchMap(() =>
          withAbortSignal((signal) =>
            createAlertRetryWrapper(
              'Loading the progression failed',
              signal,
            )(() => progressionStore.fetchProgression(jobId, signal))
              // A failed refetch must not end the stream.
              .catch(() => undefined),
          ),
        ),
      ),
    )
    this._lifecycle.manage(
      desktopNotificationState$.pipe(
        tapAction((state) => {
          this._desktopNotificationState = state
        }),
      ),
    )
  }

  @computed
  get canEnableNotifications() {
    return (
      !this._desktopNotificationState.enabled &&
      !this._desktopNotificationState.denied
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  @action.bound
  enableNotifications() {
    void this._setDesktopNotificationsEnabled(true)
      .catch(this._createAlertErrorHandler('Enabling notifications failed'))
      .catch(() => undefined)
  }
}
